<template>
  <div class="c-rich-text-area">
    <label v-if="label" :for="textAreaId" data-test-label>{{ label }}</label>
    <div v-if="wysiwyg">
      <div
        class="c-rich-text-area__editor"
        :class="{
          'c-rich-text-area__editor--readonly': readonly,
          'c-rich-text-area__editor--disabled': disabled,
        }"
      >
        <div ref="editor" :value="value" data-test-wysiwyg></div>
      </div>
    </div>
    <textarea
      v-else
      :id="textAreaId"
      v-model="value"
      :maxlength="maxContentLength"
      :placeholder="placeholder"
      class="c-rich-text-area__text-area"
      :class="{
        'c-rich-text-area__text-area--readonly': readonly,
        'c-rich-text-area__text-area--disabled': disabled,
      }"
      :disabled="disabled"
      :readonly="readonly"
      @change="onTextChange"
      @keyup="onTextChange"
      @paste="onTextChange"
    />
    <span data-test-content-length class="c-rich-text-area__content-length">
      <slot name="content-length" :contentLength="contentLength" :visibleContentLength="visibleContentLength" />
    </span>
  </div>
</template>

<script>
/**
 * Remplacement local d'`AdnTextArea` (@b2ag/design-system) : éditeur de texte enrichi bâti sur
 * Quill, avec un mode `<textarea>` simple et un compteur de caractères exposé en slot.
 * ui-kit n'expose aucun textarea ni éditeur riche, ni en 0.200 ni en 4.x.
 *
 * Portage à l'identique du composant du design system, à quatre différences près :
 * - les icônes undo/redo sont inlinées plutôt que chargées via `!!html-loader!quill/assets/…`,
 *   ce qui évite une syntaxe de loader webpack que Jest ne sait pas résoudre ;
 * - l'identifiant généré est enfin porté par le `<textarea>`, que le `<label for>` d'origine
 *   ciblait dans le vide ;
 * - les jetons SCSS du design system sont figés en valeurs (cf. bloc `<style>`) ;
 * - le mode lecture seule d'origine — une prop `edition` et un rendu du contenu en `v-html` — n'est
 *   pas repris : les trois appelants éditent tous, `edition` y valait toujours `true`. Semgrep
 *   signalait à juste titre ce `v-html`, invisible tant que le composant vivait dans
 *   `node_modules` : plutôt qu'une dérogation sur du code jamais atteint, la branche est retirée.
 *   Un futur besoin d'affichage seul passera par un rendu assaini (`sanitize-html` est déjà une
 *   dépendance du package).
 *
 * Le composant reste en JavaScript : `tsconfig` compile en `module: CommonJS`, où ts-loader
 * dégraderait l'`import()` dynamique en `require` et ferait disparaître le chunk `quill.lib`,
 * remettant Quill dans le bundle principal.
 */

// Icônes de la barre d'outils Quill, absentes de son jeu par défaut (quill/assets/icons/*.svg).
const UNDO_ICON = `<svg viewbox="0 0 18 18">
  <polygon class="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10"></polygon>
  <path class="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"></path>
</svg>`

const REDO_ICON = `<svg viewbox="0 0 18 18">
  <polygon class="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10"></polygon>
  <path class="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"></path>
</svg>`

const classicalToolbarContainer = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ header: [1, 2, false] }],
  [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['link'],
  ['undo', 'redo'],
]

const classicalFormats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'header',
  'align',
  'list',
  'bullet',
  'indent',
  'link',
]

const liteModeToolbarContainer = [
  ['bold', 'italic', 'underline'],
  [{ header: [1, 2, false] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
  ['undo', 'redo'],
]

const liteModeFormats = ['bold', 'italic', 'underline', 'header', 'list', 'bullet', 'link']

let instanceCount = 0

export default {
  name: 'CRichTextArea',
  props: {
    label: {
      type: String,
      default: null,
    },
    initialValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: null,
    },
    maxContentLength: {
      type: Number,
      default: null,
    },
    wysiwyg: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    truncateVisible: {
      type: Boolean,
      default: true,
    },
    liteModeToolbar: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    editor: null,
    contentLength: 0,
    visibleContentLength: 0,
    textAreaId: null,
    value: null,
    previousValue: null,
  }),
  mounted() {
    instanceCount += 1
    this.textAreaId = `c-rich-text-area-${instanceCount}`
    this.initializeData()
  },
  methods: {
    async initializeData() {
      this.value = this.initialValue
      this.previousValue = this.initialValue
      if (this.wysiwyg) {
        await this.setEditor()
        await this.setEditorContent()
        this.bindTextChange()
        if (this.disabled) {
          this.editor.enable(false)
        }
      } else if (this.maxContentLength > 0) {
        this.value = this.value.substring(0, this.maxContentLength)
      }
      this.countCharacters()
    },
    async setEditor() {
      const { default: Quill } = await import(/* webpackChunkName: "quill.lib" */ 'quill')
      const icons = Quill.import('ui/icons')
      icons.undo = UNDO_ICON
      icons.redo = REDO_ICON
      const options = {
        modules: {
          toolbar: {
            container: this.liteModeToolbar ? liteModeToolbarContainer : classicalToolbarContainer,
            handlers: {
              undo: () => {
                return this.editor && this.editor.history.undo()
              },
              redo: () => {
                return this.editor && this.editor.history.redo()
              },
            },
          },
        },
        readOnly: this.readonly,
        theme: 'snow',
        placeholder: this.placeholder,
        formats: this.liteModeToolbar ? liteModeFormats : classicalFormats,
      }
      this.editor = new Quill(this.$refs.editor, options)
    },
    async setEditorContent() {
      this.editor.root.innerHTML = this.value
      await this.$nextTick()
      await this.countCharacters()
      if (this.truncateVisible && this.maxContentLength && this.visibleContentLength > this.maxContentLength) {
        this.editor.deleteText(this.maxContentLength, this.visibleContentLength)
      }
    },
    bindTextChange() {
      this.editor.on('text-change', this.onTextChange)
    },
    onTextChange() {
      this.countCharacters()
      if (this.wysiwyg) {
        if (this.maxCharactersReached()) {
          this.value = this.previousValue
          this.editor.root.innerHTML = this.previousValue
          this.editor.deleteText(this.maxContentLength, this.editor.getLength())
        } else {
          this.previousValue = this.editor.root.innerHTML
          this.$emit('input', `${this.editor.root.innerHTML}`)
        }
      }
      if (!this.wysiwyg) {
        if (this.maxCharactersReached()) {
          this.value = this.value.substring(0, this.maxContentLength)
        } else {
          this.$emit('input', this.value)
        }
      }
    },
    countCharacters() {
      this.contentLength = this.wysiwyg
        ? (this.editor.root.innerHTML && this.editor.root.innerHTML.length) || 0
        : this.value.length
      this.visibleContentLength = this.wysiwyg ? this.editor.getLength() - 1 : this.value.length
    },
    maxCharactersReached() {
      return (
        this.maxContentLength &&
        (this.truncateVisible
          ? this.visibleContentLength > this.maxContentLength
          : this.contentLength > this.maxContentLength)
      )
    },
  },
}
</script>

<style scoped lang="scss">
@import '~quill/dist/quill.snow.css';

// Jetons repris d'`AdnTextArea`. Les gris du design system (#8599ad, #d5dce3, #536975) et son
// échelle typographique (m = 1.6rem, s = 1.4rem, décalée d'un cran par rapport à ui-kit, où
// --idf-font-size-m vaut 1.4rem) n'ont pas d'équivalent en 0.200 : ils sont figés ici. Seuls le
// blanc et le rayon de bordure, identiques au jeton ui-kit, passent par les variables du thème.
$c-rich-text-area-color-grey: #8599ad;
$c-rich-text-area-color-grey-light: #d5dce3;
$c-rich-text-area-color-grey-dark: #536975;
$c-rich-text-area-color-black: #141414;
$c-rich-text-area-font-size-m: 1.6rem;
$c-rich-text-area-font-size-s: 1.4rem;
$c-rich-text-area-shadow: 0.1rem 0.1rem 0.3rem -0.1rem rgba($c-rich-text-area-color-black, 0.2);
.c-rich-text-area {
  width: 100%;
  height: 100%;
  &__content-length {
    color: $c-rich-text-area-color-grey;
    font-size: $c-rich-text-area-font-size-s;
  }
  :deep(.ql-editor) {
    font-size: $c-rich-text-area-font-size-m;
    min-height: 15rem;
    overflow-y: auto;
  }
  :deep(.ql-toolbar.ql-snow) {
    background: $c-rich-text-area-color-grey;
    border: 1px solid $c-rich-text-area-color-grey-dark;
    border-radius: var(--idf-border-radius-s) var(--idf-border-radius-s) 0px 0px;
  }
  .ql-container.ql-snow {
    background: var(--idf-color-neutral-100);
    border: 1px solid $c-rich-text-area-color-grey-dark;
    border-radius: 0px 0px var(--idf-border-radius-s) var(--idf-border-radius-s);
    border-top: none;
  }
  &__text-area {
    resize: none;
    width: 100%;
    height: 100%;
    color: $c-rich-text-area-color-grey-dark;
    outline: none;
    border-radius: var(--idf-border-radius-s);
    padding: 1rem;
    &:hover {
      box-shadow: $c-rich-text-area-shadow;
    }
    &:focus-within {
      box-shadow: $c-rich-text-area-shadow;
      color: $c-rich-text-area-color-black;
    }
    &--disabled {
      box-shadow: none;
      border-color: $c-rich-text-area-color-grey-light;
      color: $c-rich-text-area-color-grey-light;
      cursor: not-allowed;
      &:hover {
        box-shadow: none;
      }
    }
    &--readonly {
      &:hover {
        box-shadow: none;
      }
    }
  }
  &__editor {
    &:hover {
      box-shadow: $c-rich-text-area-shadow;
    }
    &:focus-within {
      box-shadow: $c-rich-text-area-shadow;
    }
    &--readonly {
      &:hover {
        box-shadow: none;
      }
    }
    &--disabled {
      cursor: not-allowed;
      :deep(.ql-editor > *),
      :deep(.ql-toolbar button),
      :deep(.ql-picker-label) {
        cursor: not-allowed;
      }
      :deep(.ql-editor) {
        background-color: $c-rich-text-area-color-grey-light;
      }
      &:hover {
        box-shadow: none;
      }
    }
  }
}
</style>
